import { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import PrismicImage from '@components/cms/PrismicImage';
import Heading from '@components/ui/Heading';
import {
  CategoryNavCarouselItem,
  PageType,
} from '@adapters/prismic/types/slices';
import { eePromoImpHandler } from '@utils/gtm';
import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';
import useObjectVisibility from '@hooks/useObjectVisibility';
import { CAT_NAV_CAROUSEL } from '../index';
import Button from '@components/ui/Button';
import MarkdownCopy from '@components/cms/Slices/MarkdownCopy';
import { useLocale } from '@hooks/useLocale';
import styles from './CategoryNavItem.module.scss';

const CategoryNavItem = ({
  item,
  trackPromos,
  pageType,
  sliceIndex,
  index,
  brandedStoreCoopAttribute,
  handleClick,
}: {
  item: CategoryNavCarouselItem;
  trackPromos?: boolean;
  pageType: PageType;
  sliceIndex: number;
  index: number;
  brandedStoreCoopAttribute?: string | null;
  handleClick: (
    event: React.MouseEvent<HTMLElement | HTMLAnchorElement>
  ) => void;
}) => {
  const locale = useLocale();
  const ref = useRef<HTMLElement | null>(null);
  const isVisible = useObjectVisibility(ref);
  const { url, image, heading, cta_text: ctaText, body } = item;

  const promoDetails = useMemo(
    () => ({
      id: item?.heading,
      name: `${item?.heading}-${pageType}-${CAT_NAV_CAROUSEL}`,
      type: 'image',
      url: item?.url,
      position: `${pageType}-${sliceIndex}-${CAT_NAV_CAROUSEL}${index}`,
    }),
    [item?.heading, item?.url, pageType, sliceIndex, index]
  );

  useEffect(() => {
    if (trackPromos && isVisible) {
      eePromoImpHandler(promoDetails);

      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        page: {
          path: window.location.pathname,
          page_type: determinePageType(),
        },
        interaction: {
          coop_brand: brandedStoreCoopAttribute || null,
          interaction_location: InteractionLocation.CatNavCarousel,
        },
        ecommerce: {
          creative_name: `image,${image?.url}`,
          creative_slot: buildCreativeSlotName({
            sliceIndex,
            interactionLocation: InteractionLocation.CatNavCarousel,
            itemIndex: index,
          }),
          promotion_id: heading || '',
          promotion_name: heading || '',
          isVideo: false,
        },
      });
    }
  }, [
    isVisible,
    trackPromos,
    promoDetails,
    image?.url,
    heading,
    sliceIndex,
    index,
    brandedStoreCoopAttribute,
  ]);

  if (!image || !url) {
    return null;
  }

  const component = (
    <article
      ref={ref}
      className={cn(styles.boxContainer, {
        [styles.noCta]: !ctaText,
      })}
    >
      <div className={styles.imageContainer}>
        <PrismicImage src={image?.url} alt={image.alt ?? ''} layout="fill" />
      </div>

      {heading && (
        <Heading semanticLevel="h3" styleLevel="4" className={styles.heading}>
          {heading}
        </Heading>
      )}
      {body && body.length !== 0 && (
        <MarkdownCopy text={body[0]?.text ?? ''} className={styles.copy} />
      )}
      {ctaText && url && (
        <Button
          href={url}
          label={ctaText}
          variant="tertiary"
          size="large"
          className={styles.ctaButton}
          handleClick={handleClick}
        />
      )}
    </article>
  );

  return ctaText ? (
    component
  ) : (
    <a href={`/${locale}${item?.url}`} className={styles.cardLink}>
      {component}
    </a>
  );
};

export default CategoryNavItem;
